import { createPortal } from 'react-dom';
import { PRIVACY_POLICY_URL } from '@config/routes';
import { Button } from '@shared/ui';
import { useConsentStore } from '../consentStore';
import { shouldShowBanner } from '../consentPolicy';

/**
 * Non-blocking cookie-consent banner. Shown ONLY to in-scope (EU/EEA) users with no decision under the
 * current policy version (`shouldShowBanner`); a returning/decided user never sees it. It does NOT block
 * the app (no scrim, corner bar) — only the Hotjar tag waits on the choice. Site-theme tokens, dark/light.
 *
 * Actions: Accept all → analytics on; Reject non-essential → analytics off; Manage → the granular
 * preferences dialog (mounted globally in PrivacyLayer, opened via the store).
 */
export function ConsentBanner() {
  const scope = useConsentStore((s) => s.scope);
  const consent = useConsentStore((s) => s.consent);
  const decide = useConsentStore((s) => s.decide);
  const openPreferences = useConsentStore((s) => s.openPreferences);

  if (!shouldShowBanner(scope, consent)) return null;

  return createPortal(
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center p-3 sm:p-4"
    >
      <div
        className="flex w-full max-w-4xl flex-col gap-3 rounded-2xl bg-canvas-raised p-4 shadow-card sm:flex-row sm:items-center sm:gap-4"
        style={{ border: '1px solid var(--canvas-divider)' }}
      >
        <p className="min-w-0 flex-1 text-[13px] leading-5 text-text-muted">
          We use cookies. Strictly-necessary cookies keep you signed in; with your consent we also use
          analytics (Hotjar) to improve the journal. See our{' '}
          {/* DISABLED since 2026-08-24: the in-app cookie policy was removed pending a replacement
              (user: "remind me about it on a later stage") — the name stays, nothing to click. */}
          <span aria-disabled="true" className="font-semibold text-text-muted cursor-default">Cookie Policy</span>
          {' '}and{' '}
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className="font-semibold text-text underline underline-offset-2">Privacy Policy</a>.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" onClick={openPreferences}>Manage</Button>
          <Button variant="secondary" onClick={() => decide(false)}>Reject non-essential</Button>
          <Button className="consent-accept" onClick={() => decide(true)}>Accept all</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
